<template>
  <v-container class="ma-0 pa-0" v-if="challengesLoaded">
    <NodePinsModal />
    <NodeModal />
    <LinkModal />
    <ThoughtCatcher v-show="thoughtCatcherTab.isActive"/>
    <Participants v-show="participantsTab.isActive"/>
    <ParkingLot v-show="parkingLotTab.isActive"/>
    <Help v-show="helpTab.isActive"/>
    <Facts v-show="factsTab.isActive"/>
    <Diagram />
    <ChatFAB/>
    <AddShapeDrawer v-if="currentlyViewedChallenge.isFacilitator || currentlyViewedChallenge.isCofacilitator" />
    <BaseSnackbar
      text="Connection Lost. Reconnecting."
      :visible="snackBarVisible"
      @close="snackBarVisible = false"
    />
  </v-container>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'

import ThoughtCatcher from '@/views/whiteboard/drawer/thoughtCatcher/ThoughtCatcher'
import Participants from '@/views/whiteboard/drawer/participants/Participants'
import ParkingLot from '@/views/whiteboard/drawer/parkingLot/ParkingLot'
import Help from '@/views/whiteboard/drawer/Help/Help'
import Facts from '@/views/whiteboard/drawer/facts/FactsList'

import Diagram from '@/views/whiteboard/canvas/Diagram'
import ChatFAB from '@/views/whiteboard/canvas/ChatFAB'
import NodePinsModal from '@/views/whiteboard/canvas/NodePinsModal'
import NodeModal from '@/views/whiteboard/canvas/NodeModal'
import LinkModal from '@/views/whiteboard/canvas/LinkModal'
import AddShapeDrawer from '@/views/whiteboard/addShapeDrawer/AddShapeDrawer'

import BaseSnackbar from '@/components/base/BaseSnackbar'

export default {
  name: 'App',

  components: {
    ThoughtCatcher,
    Participants,
    Diagram,
    ChatFAB,
    ParkingLot,
    NodePinsModal,
    NodeModal,
    LinkModal,
    Help,
    Facts,
    AddShapeDrawer,
    BaseSnackbar
  },

  data: () => ({
    challengesLoaded: false,
    snackBarVisible: false,
    canvasHeartbeatInterval: null,
    eventHeartbeatInterval: null
  }),

  async created () {
    await this.requestUserInfo()
    await this.requestAllOrganizations()

    const challengeResponse = await this.requestCurrentlyViewedChallenge(this.$route.params.id)
    await this.connectCanvasSocket()
    await this.connectEventSocket()
    await this.requestChatHistory()
    await this.requestNotificationHistory()
    if (challengeResponse.success === false) {
      this.$router.push('/admin-dashboard/active-challenges')
    }
    this.challengesLoaded = true
  },

  async beforeDestroy () {
    this.setCurrentlyViewedChallenge({})
    this.removeCanvasSocket()
    this.setEventSocket(null)
    clearInterval(this.eventHeartbeatInterval)
    clearInterval(this.canvasHeartbeatInterval)
  },

  mounted () {
    console.log('whiteboard mounted')
  },

  computed: {
    ...mapState({
      currentlyViewedChallenge: state => state.challenge.currentlyViewedChallenge,
      canvasSocket: state => state.canvas.socket,
      eventSocket: state => state.event.socket
    }),
    thoughtCatcherTab () {
      return this.$store.state.tabs.whiteboard.thoughtCatcherTab
    },
    participantsTab () {
      return this.$store.state.tabs.whiteboard.participantsTab
    },
    parkingLotTab () {
      return this.$store.state.tabs.whiteboard.parkingLotTab
    },
    addShapeTab () {
      return this.$store.state.tabs.whiteboard.addShapeTab
    },
    helpTab () {
      return this.$store.state.tabs.whiteboard.helpTab
    },
    audioSettingsTab () {
      return this.$store.state.tabs.whiteboard.audioSettingsTab
    },
    factsTab () {
      return this.$store.state.tabs.whiteboard.factsTab
    },
    challengeName () {
      return this.currentlyViewedChallenge.name
    }
  },

  methods: {
    ...mapActions([
      'requestUserInfo',
      'requestAllOrganizations',
      'requestCurrentlyViewedChallenge',
      'authorizeWebsocket',
      'setupEventSocket',
      'requestChatHistory',
      'requestNotificationHistory'
    ]),
    ...mapActions('canvas', ['setupCanvasSocket']),
    ...mapMutations(['setEventSocket', 'setCurrentlyViewedChallenge']),
    ...mapMutations('canvas', ['removeCanvasSocket']),

    wait (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    async connectCanvasSocket () {
      try {
        const socketToken = await this.authorizeWebsocket()
        await this.setupCanvasSocket(socketToken)
        this.canvasHeartbeatInterval = setInterval(() => this.canvasSocket.send('heartbeat'), 3300)
        this.canvasSocket.onerror = (event) => { this.canvasSocket.close() }
        this.canvasSocket.onclose = this.sleepAndReconnectCanvasSocket
        this.snackBarVisible = false
      } catch (err) {
        this.sleepAndReconnectCanvasSocket()
      }
    },

    async connectEventSocket () {
      try {
        const socketToken = await this.authorizeWebsocket()
        await this.setupEventSocket(socketToken)
        this.eventHeartbeatInterval = setInterval(() => this.eventSocket.send('heartbeat'), 3300)
        this.eventSocket.onerror = (event) => { this.eventSocket.close() }
        this.eventSocket.onclose = this.sleepAndReconnectEventSocket
        this.snackBarVisible = false
      } catch (err) {
        this.sleepAndReconnectEventSocket()
      }
    },

    async sleepAndReconnectCanvasSocket () {
      if (this.canvasSocket !== null) {
        if (this.canvasSocket.readyState !== WebSocket.CLOSED ||
        this.canvasSocket.readyState !== WebSocket.CLOSING) {
          this.canvasSocket.close()
        }
      }
      clearInterval(this.canvasHeartbeatInterval)
      this.canvasSocket.onclose = null
      this.snackBarVisible = true
      await this.wait(1423)
      await this.connectCanvasSocket()
    },

    async sleepAndReconnectEventSocket () {
      if (this.eventSocket !== null) {
        if (this.eventSocket.readyState !== WebSocket.CLOSED ||
        this.eventSocket.readyState !== WebSocket.CLOSING) {
          this.eventSocket.close()
        }
      }
      clearInterval(this.eventHeartbeatInterval)
      this.eventSocket.onclose = null
      this.snackBarVisible = true
      await this.wait(1423)
      await this.connectEventSocket()
    }

    // async sleepAndReconnectSockets () {
    //   clearInterval(this.canvasHeartbeatInterval)
    //   clearInterval(this.eventHeartbeatInterval)
    //   this.canvasSocket.onclose = null
    //   this.eventSocket.onclose = null
    //   this.snackBarVisible = true
    //   await this.wait(1423)
    //   await this.connectSockets()
    // }
  }
}
</script>
<style lang="sass" scoped>

.tab
  ::-webkit-scrollbar
    width: 10px

  ::-webkit-scrollbar-track
    background: #f1f1f1

  ::-webkit-scrollbar-thumb
    background: #888

  ::-webkit-scrollbar-thumb:hover
    background: #555

</style>
